import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { activateToken, logout } from '../api/magicLinkApi';
import { useSession } from '../contexts/SessionContext';
import useTexts from '../hooks/useTexts';

interface ActivatePageProps {
  token: string | null;
}

/**
 * Gateway page: consumes activation token and redirects based on tokenType.
 * - NewKeyActivation → /keys/detail?activated=1 (goes straight to edit form)
 * - EditAccess → /keys (auto-redirects to detail if only one key)
 */
function ActivatePage({ token }: ActivatePageProps) {
  const [error, setError] = useState<string>('');
  const activationAttempted = useRef(false);
  const navigate = useNavigate();
  const texts = useTexts();
  const { setAuthenticated } = useSession();

  useEffect(() => {
    if (activationAttempted.current) return;
    activationAttempted.current = true;

    const activate = async () => {
      try {
        if (!token) {
          // No token — try restoring session
          navigate('/keys', { replace: true });
          return;
        }

        const result = await activateToken(token);

        // activateToken už uložil sessionId + expiresAt do sessionStorage.
        // Tady jen synchronizujeme SessionContext kvůli countdownu v headeru.
        if (result.expiresAt) {
          setAuthenticated(true, new Date(result.expiresAt).getTime());
        }

        if (result.tokenType === 'NewKeyActivation' && result.apiKey) {
          // Go straight to edit form for the activated key
          localStorage.setItem('last_selected_api_key', result.apiKey.apiKey);
          sessionStorage.setItem('activation_result', JSON.stringify(result));
          navigate('/keys/detail?activated=1', { replace: true });
        } else {
          // Fresh EditAccess link — drop remembered selection so KeysPage
          // doesn't skip the list based on a key chosen in a previous session.
          localStorage.removeItem('last_selected_api_key');
          navigate('/keys', { replace: true });
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        if (errorMessage === 'TOKEN_ALREADY_USED') {
          setError(texts.ERR_TOKEN_ALREADY_USED);
        } else if (errorMessage === 'TOKEN_EXPIRED') {
          setError(texts.ERR_TOKEN_EXPIRED);
        } else {
          setError(texts.ERR_INVALID_LINK);
        }
      }
    };

    activate();
  }, []);

  const handleClose = async () => {
    await logout();
    window.location.href = 'https://www.ppl.cz';
  };

  if (error) {
    return (
      <Box
        sx={{
          mx: 'auto',
          my: { sm: 0, md: 4 },
        }}
      >
        <Box
          sx={{
            backgroundColor: '#ffffff',
            borderLeft: '6px solid',
            borderColor: 'error.main',
            p: 3,
            float: 'left',
            mt: { sm: 0, md: 6 },
            mr: { sm: 0, md: 3 },
            width: { sm: '100%', md: 640 },
            mb: 3,
          }}
        >
          <Box mb={3}>
            <Typography
              variant="body1"
              component="span"
              sx={{ fontWeight: 'bold' }}
            >
              {texts.ACTIVATION_ERROR}
            </Typography>
            <br />
            <Typography variant="body1" component="span">
              {error}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            component="span"
            color="primary.main"
            onClick={handleClose}
            sx={{
              fontWeight: 'bold',
              textDecoration: 'underline',
              cursor: 'pointer',
              opacity: 1,
              fontSize: 16,
            }}
          >
            {texts.BACK}
          </Typography>
        </Box>
        <Box textAlign={{ xs: 'center', md: 'unset' }}>
          <img src="/img/worker.png" alt="PPL" width={350} height={350} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <CircularProgress size={48} sx={{ mb: 3 }} />
      <Typography>{texts.ACTIVATING}</Typography>
    </Box>
  );
}

export default ActivatePage;
